import React, { useEffect, useState, useRef } from 'react';
import { company } from '../../mock';
import { Menu, X, ShieldCheck, LogIn, User, ChevronDown, Phone } from 'lucide-react';
import { Button } from '../ui/button';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n';
import LanguageSwitcher from './LanguageSwitcher';
import { usePublicAuth } from '../../context/PublicAuth';

const Navbar = ({ onBookCall }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = usePublicAuth();

  // Primary links shown on the bar (kept short and scannable)
  const primaryLinks = [
    { key: 'home', href: '/#home' },
    { key: 'services', href: '/#services' },
    { key: 'portfolio', href: '/#portfolio' },
    { key: 'pricing', href: '/pricing' },
    { key: 'careers', href: '/careers', label: 'Careers' },
  ];
  // Overflow items live inside a "More" dropdown
  const moreLinks = [
    { key: 'about', href: '/#about' },
    { key: 'industries', href: '/#industries' },
    { key: 'blog', href: '/blog', label: 'Blog' },
    { key: 'insights', href: '/#insights' },
    { key: 'contact', href: '/#contact' },
  ];
  const allMobileLinks = [...primaryLinks, ...moreLinks];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleNav = (e, href) => {
    if (href.startsWith('/#')) {
      const id = href.slice(2);
      if (location.pathname !== '/') {
        e.preventDefault();
        navigate('/');
        setTimeout(() => {
          const el = document.getElementById(id);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 80);
      } else {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
      setOpen(false);
      setMoreOpen(false);
    }
  };

  const isOnHome = location.pathname === '/';
  const isTransparent = isOnHome && !scrolled;

  const linkClass = 'text-[13px] font-medium text-slate-200/90 hover:text-orange-400 transition-colors';

  const renderLink = (l, cls = linkClass) =>
    l.href.startsWith('/#') ? (
      <a key={l.href} href={l.href} onClick={(e) => handleNav(e, l.href)} className={cls}>
        {t(`nav.${l.key}`)}
      </a>
    ) : (
      <Link key={l.href} to={l.href} className={cls} onClick={() => setMoreOpen(false)}>
        {l.label || t(`nav.${l.key}`)}
      </Link>
    );

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparent
          ? 'bg-transparent'
          : 'bg-slate-950/85 backdrop-blur-xl border-b border-white/5 shadow-[0_1px_0_rgba(255,255,255,0.03)]'
      } ${scrolled ? 'h-14' : 'h-16'}`}
    >
      <div className={`max-w-7xl mx-auto px-5 lg:px-8 h-full flex items-center justify-between gap-4`}>
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo">
          <img src={company.logo} alt="Hipro Technologies" className={`w-auto rounded transition-all duration-300 ${scrolled ? 'h-8' : 'h-9'}`} />
        </Link>

        {/* Center nav */}
        <nav className="hidden lg:flex items-center gap-6 flex-1 justify-center">
          {primaryLinks.map((l) => renderLink(l))}

          <div className="relative" ref={moreRef}>
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              className={`${linkClass} inline-flex items-center gap-0.5`}
              data-testid="nav-more"
              aria-expanded={moreOpen}
            >
              More <ChevronDown className={`h-3.5 w-3.5 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
            </button>
            {moreOpen && (
              <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 min-w-[180px] rounded-xl bg-slate-900/95 backdrop-blur-xl border border-white/10 shadow-2xl py-2 animate-in fade-in slide-in-from-top-1">
                {moreLinks.map((l) => (
                  <div key={l.href} className="px-3 py-2 hover:bg-white/5">
                    {renderLink(l, 'text-[13px] font-medium text-slate-200 hover:text-orange-400 block')}
                  </div>
                ))}
              </div>
            )}
          </div>
        </nav>

        {/* Right cluster */}
        <div className="hidden lg:flex items-center gap-2 shrink-0">
          <LanguageSwitcher />

          {user ? (
            <Link
              to="/account"
              data-testid="header-account-link"
              title={user.name || user.email}
              className="flex items-center gap-1.5 text-[13px] font-semibold text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-full pl-1 pr-3 py-1 transition-colors"
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="h-6 w-6 rounded-full" />
              ) : (
                <span className="h-6 w-6 rounded-full bg-orange-500 flex items-center justify-center text-[11px] font-bold">{(user.name || user.email)[0].toUpperCase()}</span>
              )}
              <span className="max-w-[80px] truncate">{user.name?.split(' ')[0] || user.email}</span>
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="header-login-link"
              title="Sign in"
              className="h-9 w-9 flex items-center justify-center rounded-full text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
            >
              <LogIn className="h-4 w-4" />
            </Link>
          )}

          <Link
            to="/hrms/login"
            data-testid="header-hrms-login"
            title="HRMS Portal Login"
            className="h-9 w-9 flex items-center justify-center rounded-full text-white bg-white/5 hover:bg-orange-500 hover:border-orange-400 border border-white/10 transition-colors"
          >
            <ShieldCheck className="h-4 w-4" />
          </Link>

          <Button
            onClick={onBookCall}
            data-testid="header-book-call"
            className="h-9 bg-orange-500 hover:bg-orange-600 text-white text-[13px] font-semibold rounded-full px-4 shadow-[0_0_0_1px_rgba(255,255,255,0.06)]"
          >
            <Phone className="h-3.5 w-3.5 mr-1.5" /> Book Call
          </Button>
        </div>

        {/* Mobile burger */}
        <div className="lg:hidden flex items-center gap-1.5">
          <LanguageSwitcher />
          <button
            className="text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="menu"
            data-testid="mobile-menu-btn"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile sheet */}
      {open && (
        <div className="lg:hidden bg-slate-950/95 backdrop-blur-xl border-t border-white/10 animate-in fade-in slide-in-from-top-2">
          <div className="px-5 py-4 flex flex-col gap-1">
            {allMobileLinks.map((l) =>
              l.href.startsWith('/#') ? (
                <a key={l.href} href={l.href} onClick={(e) => handleNav(e, l.href)} className="text-slate-200 py-2.5 text-sm border-b border-white/5">
                  {t(`nav.${l.key}`)}
                </a>
              ) : (
                <Link key={l.href} to={l.href} onClick={() => setOpen(false)} className="text-slate-200 py-2.5 text-sm border-b border-white/5">
                  {l.label || t(`nav.${l.key}`)}
                </Link>
              )
            )}
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Link
                to="/hrms/login"
                onClick={() => setOpen(false)}
                className="flex items-center justify-center gap-1.5 text-white bg-white/5 rounded-lg py-2 px-3 text-xs font-semibold border border-white/10"
                data-testid="mobile-hrms-login"
              >
                <ShieldCheck className="h-4 w-4 text-orange-400" /> HRMS Portal
              </Link>
              {user ? (
                <Link
                  to="/account"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-account-link"
                  className="flex items-center justify-center gap-1.5 text-white bg-white/5 rounded-lg py-2 px-3 text-xs font-semibold border border-white/10"
                >
                  <User className="h-4 w-4 text-orange-400" /> {user.name?.split(' ')[0] || 'Account'}
                </Link>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-login-link"
                  className="flex items-center justify-center gap-1.5 text-white bg-white/5 rounded-lg py-2 px-3 text-xs font-semibold border border-white/10"
                >
                  <LogIn className="h-4 w-4 text-orange-400" /> Sign in
                </Link>
              )}
            </div>
            <Button
              onClick={() => {
                setOpen(false);
                onBookCall && onBookCall();
              }}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold mt-3 h-10 rounded-full"
            >
              <Phone className="h-4 w-4 mr-1.5" /> {t('nav.bookFreeCall')}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
